import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { aiConfigService } from '../../../services/aiConfigService';
import type { ThinkingMode } from '../../../services/aiConfigService';

export const useThinkingMode = () => {
  const [treeData, setTreeData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<ThinkingMode[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ThinkingMode | null>(null);
  const [formRef, setFormRef] = useState<any>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  const loadTreeData = useCallback(async () => {
    try {
      // TODO: 使用 mock 数据，后续替换为真实 API
      // 思考方式独立管理，不再显示返回格式子节点
      const thinkingModesResult = await aiConfigService.getThinkingModeList().catch(() => {
        // Mock 数据
        return [
          { id: 'analyze', label: '数据分析', category: 'analysis' },
          { id: 'predict', label: '预测分析', category: 'prediction' },
          { id: 'optimize', label: '优化建议', category: 'optimization' },
        ];
      });
      
      // 确保 thinkingModes 是数组
      const thinkingModes: ThinkingMode[] = Array.isArray(thinkingModesResult) 
        ? thinkingModesResult 
        : [];
      
      // 按分类分组
      const categories = ['analysis', 'prediction', 'optimization'];
      const tree = categories.map((category) => {
        const modes = thinkingModes.filter((tm) => tm.category === category);
        return {
          key: `category:${category}`,
          title: category === 'analysis' ? '分析类' : category === 'prediction' ? '预测类' : '优化类',
          type: 'category',
          children: modes.map((tm) => ({
            key: `thinking-mode:${tm.id}`,
            title: `${tm.label} (${tm.id})`,
            type: 'thinking-mode',
            data: tm,
            isLeaf: true,
          })),
        };
      }).filter(node => node.children.length > 0);
      
      setTreeData(tree);
    } catch (err: any) {
      message.error(`加载树形数据失败: ${err?.message}`);
    }
  }, []);

  const loadTableData = useCallback(async () => {
    setLoading(true);
    try {
      // TODO: 使用 mock 数据，后续替换为真实 API
      const data = await aiConfigService.getThinkingModeList().catch(() => {
        // Mock 数据
        return [
          { 
            id: 'analyze', 
            label: '数据分析', 
            category: 'analysis',
            description: '分析数据趋势、异常和关键发现',
            positiveWords: ['总结', '概述', '要点'],
            negativeWords: ['猜测', '假设', '可能'],
            isActive: true,
          },
          { 
            id: 'predict', 
            label: '预测分析', 
            category: 'prediction',
            description: '基于历史数据预测未来趋势',
            positiveWords: ['预测', '趋势', '可能性'],
            negativeWords: ['确定', '必然'],
            isActive: true,
          },
          { 
            id: 'optimize', 
            label: '优化建议', 
            category: 'optimization',
            description: '提供优化建议和改进方案',
            positiveWords: ['建议', '优化', '改进'],
            negativeWords: ['必须', '强制'],
            isActive: true,
          },
        ];
      });
      setTableData(data);
    } catch (err: any) {
      message.error(`加载数据失败: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = useCallback((keys: React.Key[]) => {
    if (keys.length === 0) {
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys(keys);
  }, []);

  const handleOpenModal = useCallback((record?: ThinkingMode | null) => {
    setEditingRecord(record ?? null);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setEditingRecord(null);
    formRef?.resetFields?.();
  }, [formRef]);

  const handleSave = useCallback(async () => {
    if (!formRef) return;
    try {
      const values = await formRef.validateFields();
      const payload = {
        ...editingRecord,
        ...values,
        // 正面词和负面词已经是数组格式（TagInput 返回的），直接使用
        positiveWords: Array.isArray(values.positiveWords) 
          ? values.positiveWords 
          : values.positiveWords 
          ? JSON.parse(values.positiveWords) 
          : undefined,
        negativeWords: Array.isArray(values.negativeWords) 
          ? values.negativeWords 
          : values.negativeWords 
          ? JSON.parse(values.negativeWords) 
          : undefined,
      };
      // TODO: 使用 mock 数据，后续替换为真实 API
      console.log('保存思考方式（Mock）:', payload);
      // await aiConfigService.upsertThinkingMode(payload);
      message.success('保存成功（Mock 模式）');
      handleCloseModal();
      loadTableData();
      loadTreeData();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    }
  }, [formRef, editingRecord, handleCloseModal, loadTableData, loadTreeData]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        // TODO: 实现删除 API
        message.success('删除成功');
        loadTableData();
        loadTreeData();
      } catch (err: any) {
        message.error(err?.message || '删除失败');
      }
    },
    [loadTableData, loadTreeData]
  );

  useEffect(() => {
    loadTreeData();
    loadTableData();
  }, [loadTreeData, loadTableData]);

  return {
    treeData,
    tableData,
    loading,
    modalVisible,
    editingRecord,
    setFormRef,
    expandedKeys,
    selectedKeys,
    loadTreeData,
    loadTableData,
    handleSelect,
    handleOpenModal,
    handleCloseModal,
    handleSave,
    handleDelete,
    setExpandedKeys,
  };
};
